import { Chip, Box, Typography, Stack } from '@mui/material';
import type { RaRecord } from 'react-admin';
import { plural } from '../../shared/plural';
import { InlinePair } from '../../shared/listCells';

export type LooseRecord = RaRecord & Record<string, unknown>;

const asText = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v : null;

/**
 * Nazwa przewoźnika.
 *
 * Z TMS klienta dostajemy numer i nazwę (kontakty — z kartoteki
 * kontrahentów, od 16.10.2026), adresu ani licencji nie. Pod nazwą
 * pokazujemy więc numer z ich systemu, bo to jedyny drugi ślad, po którym
 * da się firmę rozpoznać.
 */
export const NameCell = ({ record }: { record: LooseRecord }) => (
  <Box>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {asText(record.name) ?? '—'}
    </Typography>
    {record.tmsCarrierId != null && (
      <Typography variant="caption" color="text.secondary">
        nr w TMS: {String(record.tmsCarrierId)}
      </Typography>
    )}
  </Box>
);

/**
 * Nazwa z numerem w jednej linii — wersja dla listy.
 *
 * Nazwy przewoźników z TMS potrafią mieć ponad sto znaków („1.P.U.H. …
 * 2.ARTUR PETZ WSPÓLNIK SPÓŁKI CYWILNEJ …”) i robiły z wiersza trzy linie.
 * Numer z ich systemu zostaje widoczny zawsze, bo po nim firmę się
 * rozpoznaje; skraca się nazwa, a pełna jest w podpowiedzi.
 */
export const NameInlineCell = ({ record }: { record: LooseRecord }) => (
  <InlinePair
    primary={asText(record.name) ?? '—'}
    secondary={record.tmsCarrierId != null ? `nr TMS ${String(record.tmsCarrierId)}` : null}
    maxWidth={{ xs: 300, lg: 380, xl: 560 }}
  />
);

/**
 * Skąd wiemy o tym przewoźniku.
 *
 * `external` to wartość z bazy kodu oznaczająca firmę spoza własnej floty.
 * Nie tłumaczymy jej na „podwykonawca", bo to byłby nasz wniosek o roli,
 * a nie treść pola — piszemy dosłownie, co znaczy: „spoza floty”. Surowe
 * kody (`external`, `via-tms`) nic operatorowi nie mówiły (25.09.2026).
 * Nieznaną wartość pokazujemy, jak przyszła.
 */
const CARRIER_TYPE_LABELS: Record<string, string> = { external: 'spoza floty' };
/** „TMS”, nie „TMS klienta” — słownictwo z poprawki brzmienia 01.10.2026. */
const SOURCE_SYSTEM_LABELS: Record<string, string> = { 'via-tms': 'TMS', demo: 'DEMO' };

export const SourceCell = ({ record }: { record: LooseRecord }) => {
  const type = asText(record.type);
  const source = asText(record.sourceSystem);
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
      <Chip size="small" variant="outlined" label={type ? CARRIER_TYPE_LABELS[type] ?? type : '—'} />
      {source && <Chip size="small" variant="outlined" label={SOURCE_SYSTEM_LABELS[source] ?? source} />}
    </Stack>
  );
};

/** Ile zleceń przez niego przeszło — po tym widać, czy firma jest istotna. */
export const CooperationCell = ({ record }: { record: LooseRecord }) => {
  const c = record._count as { userOffers?: number } | undefined;
  const count = c?.userOffers ?? 0;
  return (
    <Typography variant="body2" sx={{ fontWeight: count > 0 ? 600 : 400, whiteSpace: 'nowrap' }}>
      {count === 0 ? 'brak zleceń' : plural(count, 'zlecenie', 'zlecenia', 'zleceń')}
    </Typography>
  );
};

/** Przewoźnik bez adresu — ten sam tekst na liście i na karcie. */
export const NO_ADDRESS = 'TMS nie podaje adresu';

/**
 * Adres.
 *
 * U przewoźników z TMS pusty, więc mówimy o tym wprost zamiast pokazywać
 * myślnik — ten wyglądałby na lukę w naszym imporcie. „TMS nie podaje”, nie
 * „klient nie podaje” — klient to dla planisty zleceniodawca (01.10.2026).
 */
export const AddressCell = ({ record }: { record: LooseRecord }) => {
  const description = [asText(record.city), asText(record.countryCode)]
    .filter(Boolean)
    .join(', ');
  return description ? (
    <span>{description}</span>
  ) : (
    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
      {NO_ADDRESS}
    </Typography>
  );
};

/**
 * Pojazdy, którymi przewoźnik jeździł dla Marathona — liczba różnych numerów
 * z TMS („informacja o samochodach” na liście, zakres minimalny 14.09).
 */
export const VehiclesCell = ({ record }: { record: LooseRecord }) => {
  const n = typeof record.vehicleCount === 'number' ? record.vehicleCount : 0;
  return n === 0 ? (
    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
      brak numerów w zleceniach
    </Typography>
  ) : (
    <span style={{ whiteSpace: 'nowrap' }}>{plural(n, 'pojazd', 'pojazdy', 'pojazdów')}</span>
  );
};

/** NIP/VAT i współpraca — nieustalona, dopóki Trade jej nie wpisze. */
export const StatusCell = ({ record }: { record: LooseRecord }) => (
  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
    {record.active === true && <Chip size="small" color="success" variant="outlined" label="współpracuje" />}
    {record.active === false && <Chip size="small" color="default" variant="outlined" label="nie współpracuje" />}
    {asText(record.taxId) && (
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        NIP/VAT {String(record.taxId)}
      </Typography>
    )}
  </Stack>
);
